<template>
  <SettingsPageAttackListSenderEditView
    v-if="result?.viewer.attackListSender.availableCorporationEntrances"
    :entrances="result.viewer.attackListSender.availableCorporationEntrances"
    :setting="setting"
    @save="handleSave"
    @cancel="handleCancel"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import router from '@/router'
import { useAlert } from '@/libs/alertPortal'
import { captureException as newrelicCaptureException } from '@/newrelic'

import SettingsPageAttackListSenderEditView from './SettingsPageAttackListSenderEditView.vue'
import type { FormData } from './SettingsPageAttackListSenderEditView.vue'
import {
  useFetchOnSettingsPageAttackListSenderCreateViewQuery,
  useCreateAttackListSenderSettingMutation,
} from './SettingsPageAttackListSenderCreateView.generated'
import { createRange } from './SettingsPageAttackListSenderEditorOptions'
import {
  AttackListSenderSettingRuleExistence,
  AttackListSenderSettingRuleGender,
} from '@/types/schema.generated'
import { useLogger } from '@/logger/logger'

const setting = {
  title: '',
  isEnabled: false,
  entranceGuid: undefined,
  body: '',
  selections: ['', ''], // 選択肢は必須なので、初めから空文字で2つ用意しておく
  age: createRange(null),
  gender: 'NOT_SPECIFIED' as AttackListSenderSettingRuleGender,
  saving: createRange(null),
  annualIncome: createRange(null),
  investment: createRange(null),
  hasPartner: 'NOT_SPECIFIED' as AttackListSenderSettingRuleExistence,
  familyAnnualIncome: createRange(null),
  hasChild: 'NOT_SPECIFIED' as AttackListSenderSettingRuleExistence,
  ageOfFirstChild: createRange(null),
  shouldSendIfSameCorporationApproached: false,
}

const { result } = useFetchOnSettingsPageAttackListSenderCreateViewQuery()

const { mutate: createAttackListSenderSetting } =
  useCreateAttackListSenderSettingMutation({})

const createSelections = (selections: string[]): string[] => {
  const filtered = selections.filter(selection => selection !== '')
  if (filtered.length === 0) {
    throw new Error('selections must not be empty')
  }
  return filtered
}

const isCompleted = ref(false)
const alert = useAlert()
const logger = useLogger()

const handleSave = async (form: FormData) => {
  if (!form.entranceGuid) {
    alert.error('プランナーを選択してください')
    return
  }

  try {
    const result = await createAttackListSenderSetting({
      input: {
        title: form.title,
        body: form.body,
        entranceGuid: form.entranceGuid,
        isEnabled: form.isEnabled,
        selections: createSelections(form.selections),
        rule: {
          savingRange: form.saving,
          annualIncomeRange: form.annualIncome,
          familyAnnualIncomeRange: form.familyAnnualIncome,
          partner: form.hasPartner,
          child: form.hasChild,
          ageOfFirstChildRange: form.ageOfFirstChild,
          gender: form.gender,
          age: form.age,
          investmentRange: form.investment,
          shouldSendIfSameCorporationApproached:
            form.shouldSendIfSameCorporationApproached,
        },
      },
    })

    const createdSetting = result?.data?.createAttackListSenderSetting
    if (!createdSetting) {
      newrelicCaptureException('createAttackListSenderSetting failed')
      alert.error(
        'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
      )
      return
    }

    logger.ltlPeople.event.createAttackListSenderSetting(logger.buildMeta(), {
      data: {
        id: Number(createdSetting.id),
        plannerId: Number(createdSetting.planner.id),
        entranceGuid: createdSetting.entrance.guid,
        isEnabled: createdSetting.isEnabled,
        rulesJson: JSON.stringify(createdSetting.rule),
        title: createdSetting.title,
        body: createdSetting.body,
        selections: createdSetting.selections,
      },
    })

    alert.success('アプローチ自動代行設定を保存しました')
    isCompleted.value = true
    // eslint-disable-next-line fp/no-mutating-methods
    router.push({ name: 'attackListSenderSetting' })
  } catch (e) {
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const handleCancel = () => {
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({ name: 'attackListSenderSetting' })
}

onBeforeRouteLeave((to, from, next) => {
  if (from.name !== 'attackListSenderSettingNew') {
    next()
    return
  }
  if (to.name === 'attackListSenderSetting' && isCompleted.value) {
    isCompleted.value = false
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value = true
      next()
    },
    onAbort: () => next(false),
  })
})
</script>
